import * as _ from 'lodash';
import { IInActiveReasonReports } from '@nkr/store/index';
import { IInActiveReasonReport, IInActiveReasonReportTotals } from '@nkr/models/index';
import { GridWrapper } from './styles';
import {
    IHeaderParams, GridReadyEvent,
    ValueGetterParams, FirstDataRenderedEvent, ICellRendererParams, SortChangedEvent,
    GridSizeChangedEvent,
    ColDef
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';

interface IProps {
    data: IInActiveReasonReports,
    onGridReady: (e: GridReadyEvent) => void
}

const InActiveReasonReportGrid = (props: IProps) => {
    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();

        props.onGridReady(e);
    };

    const getTotals = (data: IInActiveReasonReportTotals[]): IInActiveReasonReportTotals => {
        let totalReasons = _.sumBy(data, item => item.allReasons);
        let totalUnknownReasons = _.sumBy(data, item => item.unknownReasons);
        let totalPerInvalid = totalReasons > 0 ? (totalUnknownReasons / totalReasons) * 100 : 0;

        return {
            centerId: 0,
            center: 'Totals/Average',
            allReasons: totalReasons,
            unknownReasons: totalUnknownReasons,
            invalidPerc: _.round(totalPerInvalid, 1).toString() + '%',
            invalidValue: _.round(totalPerInvalid, 1)
        };
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        // get the current grids width
        let gridWidth = document.getElementById('grid-wrapper').offsetWidth;

        // keep track of which columns to hide/show
        let columnsToShow = [];
        let columnsToHide = [];

        // iterate over all columns (visible or not) and work out
        // now many columns can fit (based on their minWidth)
        let totalColsWidth = 0;
        let allColumns = e.columnApi.getAllColumns();

        for (let i = 0; i < allColumns.length; i++) {
            let column = allColumns[i];
            totalColsWidth += column.getMinWidth();

            if (totalColsWidth > gridWidth) {
                columnsToHide.push(column.getColId);
            } else {
                columnsToShow.push(column.getColId);
            }
        }

        // show/hide columns based on current grid width
        e.columnApi.setColumnsVisible(columnsToShow, true);
        e.columnApi.setColumnsVisible(columnsToHide, false);

        // fill out any available space to ensure there are no gaps
        e.api.sizeColumnsToFit();
    };

    if (props.data == null)
        return null;

    const totals = getTotals(props.data.inActiveReasonsTotals);
    const pinnedTotals = [{ rank: 0, center: totals.center, allReasons: totals.allReasons, unknownReasons: totals.unknownReasons, invalidPerc: totals.invalidPerc, invalidValue: totals.invalidValue }];

    const reportColumns: ColDef[] = [
        {
            colId: 'Rank',
            field: 'rank',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Row #
                </SortableHeaderComponent>,
            headerName: 'Row #',
            width: 45,
            sortable: false,
            suppressSizeToFit: true,
            valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
        },
        {
            colId: 'centerAlias',
            field: 'centerAlias',
            headerName: 'Center',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Center
                </SortableHeaderComponent>,
            minWidth: 120,
            maxWidth: 140,
        },
        {
            colId: 'recipientAlias',
            field: 'recipientAlias',
            headerName: 'Recipient Alias',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Recipient<br />Alias
                </SortableHeaderComponent>,
            minWidth: 100,
            maxWidth: 140,
        },
        {
            colId: 'registeredDate',
            field: 'registeredDate',
            headerName: 'Registered Date',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Registered<br />Date
                </SortableHeaderComponent>,
            minWidth: 100,
            maxWidth: 120,
        },
        {
            colId: 'recipientBloodType',
            field: 'recipientBloodType',
            headerName: 'R ABO',
            minWidth: 70,
            maxWidth: 100,
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    R ABO
                </SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IInActiveReasonReport
                return <>{data.recipientBloodType}</>
            },
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IInActiveReasonReport

                if (data.recipientBloodType == 'DNK') {
                    return '--'
                }

                return data.recipientBloodType;
            }
        },
        {
            colId: 'cpRa4',
            field: 'cpRa4',
            headerName: 'cPRA4',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    cPRA4
                </SortableHeaderComponent>,
            minWidth: 65,
            maxWidth: 100,
        },
        {
            colId: 'mosWaiting',
            field: 'mosWaiting',
            headerName: 'WT',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    WT
                </SortableHeaderComponent>,
            minWidth: 55,
            maxWidth: 120,
        },
        {
            colId: 'gender',
            field: 'gender',
            headerName: 'Gender',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Gender
                </SortableHeaderComponent>,
            minWidth: 70,
            maxWidth: 140,
        },
        {
            colId: 'chip',
            field: 'chip',
            headerName: 'Is CHIP',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Is CHIP
                </SortableHeaderComponent>,
            minWidth: 75,
            maxWidth: 140,
        },
        {
            colId: 'inActiveReason',
            field: 'inActiveReason',
            headerName: 'Inactive Reason',
            cellClass: 'center-list-cell',
            headerClass: 'center-list-header',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Inactive Reason
                </SortableHeaderComponent>,
            minWidth: 160,
            maxWidth: 200,
        },

        {
            colId: 'lastUpdated',
            field: 'lastUpdated',
            headerName: 'Last Updated',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Last<br />Updated
                </SortableHeaderComponent>,
            minWidth: 90,
            maxWidth: 140,
        }

    ];

    const reportTotalsColumns: ColDef[] = [
        {
            colId: 'center',
            field: 'centerAlias',
            headerName: 'Center',
            minWidth: 100,
            maxWidth: 140,
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Center
                </SortableHeaderComponent>,
        },
        {
            colId: 'allReasons',
            field: 'allReasons',
            headerName: 'All Reasons',
            minWidth: 100,
            maxWidth: 140,
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    All<br />Reasons
                </SortableHeaderComponent>,
        },
        {
            colId: 'unknownReasons',
            field: 'unknownReasons',
            headerName: 'Unknown Reasons',
            minWidth: 100,
            maxWidth: 120,
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    Unknown<br />Reasons
                </SortableHeaderComponent>,
        },
        {
            colId: 'invalidValue',
            field: 'invalidValue',
            headerName: '% Invalid',
            minWidth: 120,
            maxWidth: 190,
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    % Invalid
                </SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => <>{params.value}%</>
        }
    ];

    return (
        <>
            <div className='row'>
                <div className='col-sm-8'>
                    <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap' style={{ marginBottom: 20 }}>
                        <AgGridReact
                            defaultColDef={{
                                cellClass: 'center-list-cell',
                                headerClass: 'center-list-header',
                                sortable: true,
                                resizable: true
                            }}
                            columnDefs={reportColumns}
                            rowData={props.data.inActiveReasons}
                            enableCellTextSelection={true}
                            enableRangeSelection={true}
                            headerHeight={42}
                            noRowsOverlayComponentFramework={() => <div>No data for the selected date range...</div>}
                            onGridReady={onGridReady}
                            onGridSizeChanged={onGridSizeChanged}
                            onFirstDataRendered={(props: FirstDataRenderedEvent) => props.api.sizeColumnsToFit()}
                            suppressCellSelection={true}
                            onSortChanged={(e: SortChangedEvent) => e.api.refreshCells()}
                        />
                    </GridWrapper>
                </div>

                <div className='col-sm-4'>
                    <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap' style={{ marginBottom: 20 }}>
                        <AgGridReact
                            defaultColDef={{
                                cellClass: 'center-list-cell',
                                headerClass: 'center-list-header',
                                sortable: true,
                                resizable: true
                            }}
                            columnDefs={reportTotalsColumns}
                            rowData={props.data.inActiveReasonsTotals}
                            enableCellTextSelection={true}
                            enableRangeSelection={true}
                            headerHeight={42}
                            noRowsOverlayComponentFramework={() =>
                                <div>No data for the selected date range...</div>}
                            onGridReady={onGridReady}
                            onGridSizeChanged={onGridSizeChanged}
                            onFirstDataRendered={(props: FirstDataRenderedEvent) => props.api.sizeColumnsToFit()}
                            suppressCellSelection={true}
                            onSortChanged={(e: SortChangedEvent) => {
                                e.api.refreshCells();
                            }}
                            pinnedBottomRowData={pinnedTotals}
                        />
                    </GridWrapper>
                </div>
            </div>
        </>
    );
}

export default InActiveReasonReportGrid;